<template>
  <div>
    <div v-if="showFilters" class="st-filters">
      <portal :to="portalFilters" :disabled="!portal">
        <div
          class="d-flex align-items-center"
          :class="header ? 'justify-content-between' : 'justify-content-end'"
        >
          <div v-if="header" class="st-filters-header">{{ header }}</div>

          <div
            class="d-flex align-items-center justify-content-end st-filters-inputs"
          >
            <div
              v-for="filter in primaryFilters"
              :key="filter.key"
              class="pr-2 pr-sm-4"
              :style="filter.width ? { width: filter.width, minWidth: filter.width } : null"
            >
              <st-filter-input
                :filter="filter"
                :storedFilters="storedFilters"
                @change="onFilterChange"
                @clearFilter="clearChildrenFilters"
              />
            </div>

            <st-button
              v-if="moreFilters"
              variant="light"
              :callback="handleShowMoreFilters"
              customClass="border border-dark text-dark"
              class="mr-2 mr-sm-0"
            >
              {{ $t("GENERAL.BUTTON.MORE_FILTERS") }}
            </st-button>

            <template v-if="configurableFilters && configurableFilters.length > 0">
              {{ $t('GENERAL.TABLE.FILTER_SELECTOR_TITLE') }}
              <b-button
                id="filterSelectorButton"
                pill
                variant="outline"
                size="sm"
                class="mr-2 mr-sm-0"
                v-b-tooltip.hover="{ variant: 'info' }"
                :title="$t('GENERAL.TABLE.FILTER_SELECTOR_TITLE')"
              >
                <b-icon-list-check font-scale="1.5"></b-icon-list-check>
              </b-button>
              <b-popover
                target="filterSelectorButton"
                triggers="click"
                :show.sync="showFilterSelector"
                placement="bottomleft"
                custom-class="st-column-selector-popover st-filter-selector-popover"
              >
                <template #title>{{ $t('GENERAL.TABLE.FILTER_SELECTOR_TITLE') }}</template>
                <div class="st-col-config-wrapper">
                  <b-form-input
                    v-model="filterSelectorSearch"
                    :placeholder="$t('GENERAL.DUAL_LIST_BOX.SEARCH_PLACEHOLDER')"
                    class="st-col-config-search"
                    type="search"
                  />
                  <div class="st-col-config-list">
                    <div class="d-flex flex-row justify-content-between align-items-center st-col-config-item st-col-config-select-all">
                      <b-form-checkbox
                        :checked="allFiltersSelected"
                        :indeterminate="someFiltersSelected"
                        @change="toggleSelectAllFilters"
                        class="mr-4 st-col-config-checkbox"
                      >
                        {{ $t('GENERAL.TABLE.SELECT_ALL_FILTERS') }}
                      </b-form-checkbox>
                    </div>
                    <div class="st-col-config-divider"></div>
                    <div
                      class="d-flex flex-row justify-content-between align-items-center st-col-config-item"
                      v-for="configurableFilter in filteredConfigurableFilters"
                      :key="configurableFilter.key"
                    >
                      <b-form-checkbox
                        :checked="selectedConfigurableFilters.includes(configurableFilter.key)"
                        @change="handleFilterSelected($event, configurableFilter.key)"
                        class="mr-4 st-col-config-checkbox"
                      >
                        {{ configurableFilter.label }}
                      </b-form-checkbox>
                    </div>
                    <div v-if="filteredConfigurableFilters.length === 0" class="st-col-config-empty">
                      {{ $t('GENERAL.DUAL_LIST_BOX.SEARCH_RESULTS') }}
                    </div>
                  </div>
                  <div class="d-flex flex-column mt-2" style="gap: 0.4rem;">
                    <b-button @click="applyFilterSelector" size="sm" variant="primary" block>
                      {{ $t('GENERAL.TABLE.APPLY') }}
                    </b-button>
                    <b-button @click="closeFilterSelector" size="sm" variant="outline-primary" block>
                      {{ $t('GENERAL.TABLE.CANCEL') }}
                    </b-button>
                  </div>
                </div>
              </b-popover>
            </template>

            <st-button
              v-if="!moreFilters && showErase"
              variant="dark"
              :disabled="!hasActiveFilters"
              :callback="handleClearFilters"
            >
              {{ $t("GENERAL.BUTTON.ERASE") }}
            </st-button>
            <st-button
              v-if="clearButtonInPrimaryFilters"
              class="ml-0 ml-sm-4"
              variant="dark"
              :disabled="!hasActiveFilters"
              :callback="handleClearFilters"
            >
              {{ $t("GENERAL.BUTTON.ERASE") }}
            </st-button>

            <slot name="filters-toolbar"></slot>
          </div>
        </div>

        <div
          v-if="moreFilters && showMoreFilters"
          class="secondary-filters-wrapper bg-gray-200 shadow mt-2 rounded"
        >
          <div class="secondary-filters-items">
            <div
              v-for="filter in secondaryFilters"
              :key="filter.key"
              class="secondary-filter-tile"
              :style="filter.width ? { width: filter.width, minWidth: filter.width } : null"
            >
              <div class="secondary-filter-label">
                <span v-if="filter.filter_label">{{
                  filter.filter_label
                }}</span>
              </div>

              <st-filter-input
                :filter="filter"
                @change="onFilterChange"
                class="w-100"
              />
            </div>
          </div>

          <div class="secondary-filters-actions">
            <st-button
              v-if="!clearButtonInPrimaryFilters"
              variant="dark"
              :disabled="!hasActiveFilters"
              :callback="handleClearFilters"
              class="mr-2"
            >
              {{ $t("GENERAL.BUTTON.ERASE") }}
            </st-button>

            <st-button variant="light" :callback="handleHideFilters">
              <i class="far fa-times-circle p-0 text-dark"></i>
            </st-button>
          </div>
        </div>
      </portal>
    </div>

    <div>
      <portal :to="portalContent" :disabled="!portal">
        <slot></slot>
      </portal>
    </div>

    <div v-if="showPagination" class="st-pagination">
      <portal :to="portalPagination" :disabled="!portal">
        <div
          v-if="total && total > 0"
          class="d-flex align-items-center justify-content-between"
        >
          <div class="text-dark-50">
            {{ displayText }}
          </div>
          <b-pagination
            v-model="page"
            :total-rows="total"
            :per-page="rowsPerPage"
            class="mb-0"
            :prev-text="$t('GENERAL.PREV')"
            :next-text="$t('GENERAL.NEXT')"
            first-number
            last-number
          ></b-pagination>
        </div>
      </portal>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import StFilterInput from "../filters-pagination/StFilterInput.vue";
import Vue from "vue";
import { BIconListCheck, BPopover, BFormCheckbox, BFormInput, BButton, VBTooltip } from "bootstrap-vue";
import calcParams from "../../utils/calc-query-params";
import { BPagination } from "bootstrap-vue";
import calcSortParams from "../../utils/calc-sort-params";

export default {
  name: "StFiltersPagination",
  components: {
    StFilterInput,
    "b-pagination": BPagination,
    BIconListCheck,
    BPopover,
    BFormCheckbox,
    BFormInput,
    BButton,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  props: {
    filters: Array,
    configurableFilters: Array,
    selectionWarningThreshold: {
      type: Number,
      default: null,
    },
    selectionLimit: {
      type: Number,
      default: null,
    },
     persistanceIdentifier: {
      type: [String, Object],
    },  
    total: {
      type: Number,
    },
    perPage: {
      type: Number,
    },
    currentPage: Number,
    stateModule: String,
    header: {
      type: String,
      default: "",
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    showPagination: {
      type: Boolean,
      default: true,
    },
    showErase: {
      type: Boolean,
      default: true,
    },
    moreFilters: {
      type: Boolean,
      default: true,
    },
    calcParams: {
      type: Function,
      default: calcParams,
    },
    calcSortParams: {
      type: Function,
      default: calcSortParams,
    },
    portal: {
      type: Boolean,
      default: false,
    },
    portalFilters: {
      type: String,
      default: "filters",
    },
    portalContent: {
      type: String,
      default: "content",
    },
    portalPagination: {
      type: String,
      default: "pagination",
    },
    clearButtonInPrimaryFilters: {
      type: Boolean,
      default: false,
    },
    emitFilters: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showMoreFilters: false,
      showFilterSelector: false,
      selectedConfigurableFilters: [],
      snapshotSelectedConfigurableFilters: [],
      configurableFiltersHydrated: false,
      filterSelectorSearch: "",
      hasWarnedAboutSelectionCount: false,
      primaryFilters: this.getFilters(true),
      secondaryFilters: this.getFilters(false),
      sortingParams: {},
    };
  },
  created() {
    this.showMoreFilters = this.hasActiveSecondaryFilters;
    if (this.hasConfigurableFilters) {
      this.hydrateSelectedConfigurableFilters();
      this.primaryFilters = this.getFilters(true);
      this.secondaryFilters = this.getFilters(false);
    }
    this.emitParamsChangedEvent();
  },
  computed: {
    ...mapGetters({
      appConfig: "shared/appConfig",
    }),
    page: {
      get() {
        if (this.stateModule) {
          if (this.showPagination) {
            return this.$store.getters[`${this.stateModule}/currentPage`];
          }
        }
        return this.currentPage;
      },
      set(val) {
        if (this.stateModule) {
          if (this.showPagination) {
            this.$store.commit(`${this.stateModule}/setCurrentPage`, val);
          }
        } else {
          this.$emit("currentPage", val);
        }
      },
    },
    rowsPerPage() {
      return this.perPage || this.appConfig.RECORDS_PER_PAGE;
    },
    hasActiveSecondaryFilters() {
      for (const filter of this.secondaryFilters) {
        if (filter.value && typeof filter.value === "object") {
          const objActiveFilter = Object.values(filter.value).some(
            (value) => !!value
          );
          if (objActiveFilter) return true;
        }
        if (filter.value !== "" && typeof filter.value === "string") {
          return true;
        }
      }

      return false;
    },
    hasActivePrimaryFilters() {
      for (const filter of this.primaryFilters) {
        if (filter.value !== "") {
          return true;
        }
      }
      return false;
    },
    hasActiveFilters() {
      return this.hasActivePrimaryFilters || this.hasActiveSecondaryFilters;
    },
    displayText() {
      const start = (this.page - 1) * this.rowsPerPage + 1;
      let end = start + this.rowsPerPage - 1;
      end = end > this.total ? this.total : end;

      return this.$t("GENERAL.PAGINATION", { start, end });
    },
    storedFilters() {
      return this.stateModule
        ? this.$store.getters[`${this.stateModule}/filters`]
        : {};
    },
    storedSortData() {
      return this.stateModule
        ? this.$store.getters[`${this.stateModule}/sorting`]
        : {};
    },
    persistanceKey() {
      if (!this.persistanceIdentifier) return null;
      if (typeof this.persistanceIdentifier === "object") {
        return `filters_${this.persistanceIdentifier.key}`;
      }
      return `filters_${this.persistanceIdentifier}`;
    },
    hasConfigurableFilters() {
      return this.configurableFilters && this.configurableFilters.length > 0;
    },
    allFiltersSelected() {
      const selectableFilters = this.selectionLimit
        ? this.configurableFilters.slice(0, this.selectionLimit)
        : this.configurableFilters;
      return (
        this.hasConfigurableFilters &&
        selectableFilters.every((filter) =>
          this.selectedConfigurableFilters.includes(filter.key)
        )
      );
    },
    someFiltersSelected() {
      return (
        !this.allFiltersSelected &&
        this.hasConfigurableFilters &&
        this.configurableFilters.some((filter) =>
          this.selectedConfigurableFilters.includes(filter.key)
        )
      );
    },
    filteredConfigurableFilters() {
      const search = this.filterSelectorSearch.trim().toLocaleLowerCase();
      if (!search) return this.configurableFilters;

      return this.configurableFilters.filter((filter) =>
        String(filter.label || filter.key).toLocaleLowerCase().includes(search)
      );
    },
  },
  watch: {
    page() {
      this.emitParamsChangedEvent();
    },
    filters: {
      // watch for dynamic options changes
      deep: true,
      immediate: true,
      handler: function () {
        if (
          this.hasConfigurableFilters &&
          !this.configurableFiltersHydrated
        ) {
          this.hydrateSelectedConfigurableFilters();
        }
        this.primaryFilters = this.getFilters(true);
        this.secondaryFilters = this.getFilters(false);
      },
    },
    storedSortData: {
      deep: true,
      immediate: true,
      handler(newVal, oldVal) {
        this.handleSorting(newVal, oldVal);
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutsideFilterSelector);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutsideFilterSelector);
  },  
  methods: {
    initFilterValue(filter, storeFilters) {
      if (filter.type === "daterange") {
        return {
          start: storeFilters?.[filter.key]?.start || "",
          end: storeFilters?.[filter.key]?.end || "",
        };
      }

      return storeFilters?.[filter.key] || "";
    },
    hydrateSelectedConfigurableFilters() {
      const storedConfigurableFilters = this.persistanceKey
        ? localStorage.getItem(this.persistanceKey)
        : null;
      const persisted = storedConfigurableFilters
        ? JSON.parse(storedConfigurableFilters)
        : null;

      const currentFilterKeys = this.configurableFilters.map((filter) => filter.key);

      if (persisted) {
        this.snapshotSelectedConfigurableFilters = persisted.filter((key) =>
          currentFilterKeys.includes(key)
        );
      } else {
        this.snapshotSelectedConfigurableFilters = this.configurableFilters
          .filter((filter) => filter.primary)
          .map((filter) => filter.key);
      }

      this.snapshotSelectedConfigurableFilters = this.normalizeConfigurableSelection(
        this.snapshotSelectedConfigurableFilters
      );
      this.selectedConfigurableFilters = [...this.snapshotSelectedConfigurableFilters];
      this.configurableFiltersHydrated = true;
    },
    isPrimaryFilter(filter) {
      if (this.hasConfigurableFilters) {
        return this.selectedConfigurableFilters.includes(filter.key);
      }
      return filter.primary;
    },
    getFilters(primary) {
      const storeFilters = this.stateModule
        ? this.$store.getters[`${this.stateModule}/filters`]
        : {};
      return this.filters
        .filter((filter) => {
          return this.isPrimaryFilter(filter) === primary;
        })
        .map((filter) => ({
          ...filter,
          updateOptions: filter?.updateOptions,
          getOptions: filter?.getOptions,
          value: this.initFilterValue(filter, storeFilters),
        }));
    },
    emitParamsChangedEvent() {
      const params = this.calcParams({
        currentPage: this.page,
        perPage: this.rowsPerPage,
        filters: [...this.primaryFilters, ...this.secondaryFilters],
      });

      this.$emit("change", { ...params, ...this.sortingParams });

      if (this.emitFilters) {
        const filters = [...this.primaryFilters, ...this.secondaryFilters];
        this.$emit("search", filters);
      }

      if (this.stateModule) {
        Object.keys(params).forEach((key) => {
          this.$store.commit(`${this.stateModule}/setFilters`, {
            key,
            value: params[key],
          });
        });
      }
    },
    updateFilter(filter) {
      const payload = {
        key: filter.key,
        value: filter.value,
      };

      if (filter?.options) {
        const selectedFilter = filter.options.find(
          (el) => el.value === filter.value
        );
        if (selectedFilter?.combineWith) {
          payload.combineWith = selectedFilter.combineWith;
          filter.combineWith = selectedFilter.combineWith;
        } else {
          filter.combineWith = undefined;
        }
      }

      if (this.stateModule) {
        this.$store.commit(`${this.stateModule}/setFilters`, payload);
      } else {
        this.$emit("filter", payload);
      }
    },
    resetPagination() {
      if (this.page === 1) {
        this.emitParamsChangedEvent();
      } else {
        this.page = 1;
      }
    },
    clearFilter(key) {
      const all = [...this.primaryFilters, ...this.secondaryFilters];
      const target = all.find((f) => f.key === key || f.name === key);
      if (!target) return;
      Vue.set(target, "value", "");
      this.updateFilter(target);
      // sync original filters array so future re-compute respects cleared value
      const original = this.filters.find(
        (f) => f.key === target.key || f.name === target.name
      );
      if (original) original.value = "";
    },
    onFilterChange(filter) {
      this.$emit("filter-change", { key: filter.key, value: filter.value });
      //check if any other filter has to update its options depending on the current selected value
      if (filter?.children && !filter?.asyncData) {
        filter.children.forEach((child) => {
          const el = this.filters.find(
            (element) => element.name === child.name
          );
          if (filter.value) {
            el.fetchParams = { [child.key]: filter.value };
            el.updateOptions();
          } else {
            el.updateOptions(true);
            this.clearChildrenFilters(el);
          }
        });
      }

      this.updateFilter(filter);
      this.resetPagination();
    },
    handleShowMoreFilters() {
      this.showMoreFilters = true;
    },
    handleHideFilters() {
      this.showMoreFilters = false;
    },
    toggleSelectAllFilters(checked) {
      const selection = checked
        ? this.configurableFilters.map((filter) => filter.key)
        : [];
      this.setConfigurableFilterSelection(selection);
    },
    handleFilterSelected(checked, key) {
      const selection = [...this.selectedConfigurableFilters];
      if (checked) {
        selection.push(key);
      } else {
        selection.splice(selection.indexOf(key), 1);
      }
      this.setConfigurableFilterSelection(selection);
    },
    normalizeConfigurableSelection(keys) {
      const currentKeys = new Set(this.configurableFilters.map((filter) => filter.key));
      const uniqueKeys = [...new Set(keys)].filter((key) => currentKeys.has(key));

      if (!this.selectionLimit || uniqueKeys.length <= this.selectionLimit) {
        return uniqueKeys;
      }

      this.$alert({
        type: "warning",
        title: this.$t("GENERAL.TABLE.MAX_SELECTION_TITLE"),
        text: this.$t("GENERAL.TABLE.MAX_SELECTION_TEXT", { max: this.selectionLimit }),
        showConfirmButton: false,
      });
      const selectedKeys = new Set(uniqueKeys);
      return this.configurableFilters
        .map((filter) => filter.key)
        .filter((key) => selectedKeys.has(key))
        .slice(0, this.selectionLimit);
    },
    setConfigurableFilterSelection(keys) {
      const exceededLimit = this.selectionLimit && keys.length > this.selectionLimit;
      this.selectedConfigurableFilters = this.normalizeConfigurableSelection(keys);

      if (exceededLimit) {
        this.hasWarnedAboutSelectionCount = true;
      } else if (
        this.selectionWarningThreshold
        && this.selectedConfigurableFilters.length > this.selectionWarningThreshold
        && !this.hasWarnedAboutSelectionCount
      ) {
        this.$alert({
          type: "warning",
          title: this.$t("GENERAL.TABLE.RECOMMENDED_SELECTION_TITLE"),
          text: this.$t("GENERAL.TABLE.RECOMMENDED_SELECTION_TEXT", {
            selected: this.selectedConfigurableFilters.length,
            recommended: this.selectionWarningThreshold,
            max: this.selectionLimit,
          }),
          showConfirmButton: false,
        });
        this.hasWarnedAboutSelectionCount = true;
      }

      if (!this.selectionWarningThreshold || this.selectedConfigurableFilters.length <= this.selectionWarningThreshold) {
        this.hasWarnedAboutSelectionCount = false;
      }
    },
    applyFilterSelector() {
      this.snapshotSelectedConfigurableFilters = [...this.selectedConfigurableFilters];
      if (this.persistanceKey) {
        localStorage.setItem(
          this.persistanceKey,
          JSON.stringify(this.snapshotSelectedConfigurableFilters)
        );
      }
      this.primaryFilters = this.getFilters(true);
      this.secondaryFilters = this.getFilters(false);
      this.showFilterSelector = false;
    },
    closeFilterSelector() {
      this.selectedConfigurableFilters = [...this.snapshotSelectedConfigurableFilters];
      this.showFilterSelector = false;
    },
    handleClearFilters() {
      for (let filter of [...this.primaryFilters, ...this.secondaryFilters]) {
        if (filter.type === "daterange") {
          Vue.set(filter, "value", { start: "", end: "" });
        } else {
          Vue.set(filter, "value", "");
        }
        this.updateFilter(filter);
      }

      this.resetPagination();
    },
    updateFiltersOptions() {
      this.filters.forEach((filter) => {
        if (filter.dynamic) {
          filter.updateOptions();
        }
      });
    },
    clearChildrenFilters(filter) {
      const filters = [...this.primaryFilters, ...this.secondaryFilters];
      const filterToReset = filters.find((el) => el.name === filter.name);

      if (filterToReset) {
        Vue.set(filterToReset, "value", "");
        this.updateFilter(filterToReset);
      }
    },
    handleSorting(newVal, oldVal) {
      // storeSort not defined - no table sorting
      if (!oldVal && !newVal) return;
      // init
      if (!oldVal) {
        this.sortingParams = this.calcSortParams(this.storedSortData);
        return;
      }
      // reset (by click on a column without sorting option)
      if (!newVal.sortBy) {
        this.resetSorting(newVal);
        return;
      }
      this.sortingParams = this.calcSortParams(newVal);

      // reset pagination in case of switching sort columns
      if (newVal.sortBy !== oldVal.sortBy) {
        this.resetPagination();
        return;
      }

      this.emitParamsChangedEvent();
    },
    resetSorting(val) {
      this.sortingParams = {};
      this.resetPagination();
      this.$store.commit(`${this.stateModule}/setSort`, val);
    },
    handleClickOutsideFilterSelector(e) {
      if (!this.showFilterSelector) return;
      const popoverEl = document.querySelector('.st-filter-selector-popover');
      const buttonEl = document.getElementById('filterSelectorButton');
      if (
        popoverEl && !popoverEl.contains(e.target) &&
        buttonEl && !buttonEl.contains(e.target)
      ) {
        this.closeFilterSelector();
      }
    },    
  },
};
</script>

<style scoped>
.secondary-filters-wrapper {
  display: flex;
  align-items: center;
  padding: 8px 10px;
}

.secondary-filters-items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
  flex: 1 1 auto;
  min-width: 0;
}

.secondary-filter-tile {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 260px;
  min-width: 260px;
}

.secondary-filter-label {
  min-height: 14px;
  line-height: 14px;
  font-size: 11px;
}

.secondary-filters-actions {
  display: flex;
  align-items: center;
  margin-left: 12px;
  margin-right: 6px;
  flex: 0 0 auto;
}
</style>